import React, { useState, useRef, useEffect } from 'react';

const MultiSelect = ({ options, value, onChange, placeholder = "Select options", className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option => {
    const displayName = option.name || option.title || '';
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOptions = options.filter(option => value.includes(option._id));

  const handleToggleOption = (optionId) => {
    const newValue = value.includes(optionId)
      ? value.filter(id => id !== optionId)
      : [...value, optionId];
    onChange(newValue);
  };

  const removeOption = (optionId) => {
    onChange(value.filter(id => id !== optionId));
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-[42px] px-3 py-1.5 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap items-center gap-1.5 pr-8 transition-all duration-200 hover:border-gray-400 focus-within:border-[#009ef7] focus-within:ring-1 focus-within:ring-[#009ef7]"
      >
        {selectedOptions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {selectedOptions.map(option => (
              <span
                key={option._id}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full border bg-sky-50 text-sky-700 border-sky-200 shadow-sm transition-all duration-150 hover:bg-sky-100"
              >
                {option.name || option.title}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(option._id);
                  }}
                  className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-sky-200 text-sky-600 hover:text-sky-800 transition-colors font-bold text-[10px]"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        )}
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-gray-400 text-xs transition-transform duration-200`}></i>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-hidden transition-all duration-200 ease-out">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-200 focus:border-[#009ef7] focus:ring-1 focus:ring-[#009ef7] rounded-lg text-sm outline-none transition-all"
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="max-h-48 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <label
                  key={option._id}
                  className="flex items-center px-4 py-2 hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option._id)}
                    onChange={() => handleToggleOption(option._id)}
                    className="w-4 h-4 text-[#009ef7] border-gray-300 rounded focus:ring-[#009ef7] cursor-pointer mr-3 transition-all"
                  />
                  <span className="text-gray-700 text-sm font-medium">{option.name || option.title}</span>
                </label>
              ))
            ) : (
              <div className="px-4 py-2.5 text-gray-400 text-sm text-center">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
